import React, { ReactNode, InputHTMLAttributes, FormEvent } from 'react';
import MaskedInput from 'react-text-mask';

import { FieldContainer, FieldEmotion, Label, InputEmotion, Caption, Icon } from './styles';
import { Hint, viewHint } from '../Hint';

export type viewType = 'small' | 'medium' | 'big';

interface Props {
  id: string;
  label?: string;
  value?: any;
  defaultValue?: any;
  name?: string;
  view?: viewType;
  allowedSymbols?: RegExp;
  isLabelShort?: boolean;
  caption?: ReactNode;
  isDisabled?: boolean;
  mask?: (string | RegExp)[];
  icon?: ReactNode;
  note?: ReactNode;
  hint?: ReactNode;
  hintInfo?: ReactNode;
  hintError?: ReactNode;
  hintWarning?: ReactNode;
  hintView?: typeof viewHint[number];
  hintPosition?: 'default' | 'top' | 'bottom' | 'left' | 'right';
  onChange?: (e: FormEvent<HTMLInputElement>) => void;
}

export type InputProps = Props & InputHTMLAttributes<HTMLInputElement>;

export function Input(props: InputProps) {
  const {
    label,
    id,
    view = 'medium',
    isLabelShort,
    isDisabled,
    caption,
    icon,
    note,
    hint,
    hintInfo,
    hintError,
    hintWarning,
    hintView = 'error',
    hintPosition = 'right',
    allowedSymbols,
    mask,
    ...other
  } = props;

  const isHintVisible = !!hint || !!hintInfo || !!hintError || !!hintWarning;

  // Добавляем флаг /g на случай если забыли его явно передать
  const allowedSymbolsRegexp = allowedSymbols && new RegExp(allowedSymbols, 'g');

  const getHint = () => {
    let hintToRender = hint;
    let hintToRenderView = hintView;

    if (hintInfo) {
      hintToRender = hintInfo;
      hintToRenderView = 'info';
    }

    if (hintWarning) {
      hintToRender = hintWarning;
      hintToRenderView = 'warning';
    }

    if (hintError) {
      hintToRender = hintError;
      hintToRenderView = 'error';
    }

    return (
      hintToRender && (
        <Hint testId={`${props.name}-inputHint`} view={hintToRenderView} position={hintPosition}>
          {hintToRender}
        </Hint>
      )
    );
  };

  return (
    <FieldContainer
      isHintDefault={hintPosition === 'default'}
      isHintVisible={isHintVisible}
      view={view}
    >
      <FieldEmotion isDisabled={isDisabled} sizeInput={view}>
        {label && (
          <Label htmlFor={id} size={view} isShort={isLabelShort}>
            {label}
          </Label>
        )}
        <MaskedInput
          mask={mask ? mask : false}
          guide={false}
          {...props}
          render={(ref, props) => (
            <InputEmotion
              {...other}
              ref={ref as (instance: HTMLInputElement | null) => void}
              id={id}
              sizeInput={view}
              disabled={isDisabled}
              onKeyDown={event => {
                // Задача на приведение валидации в порядок - https://tracker.unitedtraders.team/browse/PAYMENT-1886
                if (props.onKeyDown) {
                  props.onKeyDown(event);
                }

                if (allowedSymbolsRegexp) {
                  const allowedKeys = ['Backspace', 'Enter', 'Delete', 'ArrowLeft', 'ArrowRight'];

                  if (
                    !allowedKeys.includes(event.key) &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.key.match(allowedSymbolsRegexp)
                  ) {
                    event.preventDefault();
                  }
                }
              }}
              onChange={event => {
                if (
                  allowedSymbolsRegexp &&
                  !event.target.value.match(allowedSymbolsRegexp) &&
                  event.target.value !== ''
                ) {
                  event.preventDefault();
                } else {
                  props.onChange && props.onChange(event);
                }
              }}
              onPaste={event => {
                const data = event.clipboardData.getData('text');

                if (allowedSymbolsRegexp && !data.match(allowedSymbolsRegexp)) {
                  event.preventDefault();
                } else {
                  props.onPaste?.(event);
                }
              }}
            />
          )}
        />
        {caption && <Caption>{caption}</Caption>}
        {icon && <Icon>{icon}</Icon>}
        {note && note}
      </FieldEmotion>

      {getHint()}
    </FieldContainer>
  );
}
